import {
  findResourceOfType,
  isResourceOfType,
  type OpalApiV3Component,
} from '@opallabs/api'
import { MenuOption } from '@opallabs/concepts'
import { useWorkspace } from '@opallabs/hooks'
import {
  type FlattenedCustomField,
  type CustomFieldsRequestParams,
  type RawCustomFieldsResponse,
} from '../types'
import { useReadCustomFields } from '../useReadCustomFields'
import { systemTypeToIcon } from '../utils'

type CustomFieldOption = OpalApiV3Component<'custom_field_option'>
type Metadata = OpalApiV3Component<'custom_field'>['attributes']['metadata']
type MetadataWithOptions = Extract<
  NonNullable<Metadata>,
  { options: unknown[] }
>

// The `custom_field` OpenAPI spec allows `metadata` to be an empty object; when we generate `OPAL_API_SCHEMA` that gets
// turned into `{ [key: string]: unknown }`--a completely arbitrary object. This function asserts that `metadata` is
// actually the subset of the type union that has an `options` array.
export function customFieldMetadataHasOptions(
  metadata: FlattenedCustomField['metadata'] | undefined
): metadata is MetadataWithOptions {
  if (!metadata) return false
  if (!('options' in metadata)) return false
  return Array.isArray(metadata.options)
}

type FlatMetadataOption = {
  id: string
  display_name?: string
  position?: number
  deleted_at?: string | null
}

/**
 * Flattens a custom field's `metadata.options` into the `MenuOption` shape used by select cells and pickers, ordered by `position`.
 * Returns `[]` for fields that don't carry options.
 *
 * Note: `metadata.options` carries no color. To show option colors, layer them
 * on from `useCustomFieldOptionMap` (id -> color).
 */
export function selectOptionsFromMetadata(
  metadata: FlattenedCustomField['metadata'] | undefined
): Array<MenuOption> {
  if (!customFieldMetadataHasOptions(metadata)) return []
  return (metadata.options as FlatMetadataOption[])
    .toSorted((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((option) => ({
      value: option.id,
      label: option.display_name ?? '',
      inactive: option.deleted_at != null,
    }))
}
// `getMetadataForCustomField` (used in `<CustomFieldsDrawer>` for the `<SelectOptions>` `options` prop)
// uses the `metadata` property returned by `selectCustomFields`, rather than using the actual `custom_field_option`
// resources `useCustomFieldsCollection` fetches. The `metadata` version of the resources is a subset of the actual
// resources; notably, the `metadata` version doesn't include the `paired_label` relationship.
//
// This function augments the `metadata` version using the real, complete resources.
function replaceMetadata(
  includedResources: RawCustomFieldsResponse['included'],
  originalMetadata: Metadata
): Metadata {
  const allOptions: CustomFieldOption[] =
    includedResources?.filter(isResourceOfType('custom_field_option', 'V3')) ??
    []

  let options: MetadataWithOptions['options'] = []
  if (customFieldMetadataHasOptions(originalMetadata)) {
    options = originalMetadata.options.map((option) => {
      const completeOption = allOptions.find(
        (resource) => resource.id === option.id
      )

      if (!completeOption?.relationships) return option

      // @note Directly reassigning `option.relationships` will cause a `TypeError`
      const { custom_field_color, paired_label } = completeOption.relationships
      return {
        ...option,
        relationships: { custom_field_color, paired_label },
      }
    })
  }

  return {
    options,
  }
}

export const selectCustomFields = (
  responseDocument: RawCustomFieldsResponse
) => {
  return responseDocument.data.map((customField) => {
    const fieldType = responseDocument.included?.find(
      findResourceOfType(
        'custom_field_type',
        'V3',
        customField.relationships.custom_field_type.data.id
      )
    )

    const adjustedMetadata = replaceMetadata(
      responseDocument.included,
      customField.attributes.metadata
    )

    return {
      id: customField.id,
      customFieldTypeId: customField.relationships.custom_field_type.data.id,
      name: customField.attributes.display_name,
      type: fieldType?.attributes.system_type,
      typeLabel: fieldType?.attributes.display_name,
      icon: fieldType && systemTypeToIcon[fieldType.attributes.system_type],
      description: customField.attributes.description,
      isInactive: customField.attributes.is_deactivated,
      isPaired: Boolean(customField.relationships.paired_label_set?.data?.id),
      isColorDisabled: customField.attributes.is_color_disabled,
      isSystemGenerated: customField.attributes.is_system_generated,
      metadata: adjustedMetadata,
      isCascading: customField.attributes.is_cascading,
      defaultNotificationMode: customField.attributes.default_notification_mode,
      pairedLabelSetId: customField.relationships.paired_label_set?.data?.id,
      createdAt: customField.attributes.created_at,
      creatorId: customField.relationships.creator.data.id,
    } as const satisfies FlattenedCustomField
  })
}

export function customFieldsCollectionParams(
  workspaceId: string
): CustomFieldsRequestParams {
  return {
    query: {
      filter: { workspace_id: [workspaceId] },
      include: [
        'custom_field_type',
        'active_options.custom_field_color',
        'deleted_options.custom_field_color',
      ],
      page: {
        limit: 500, // Increased from default 150
      },
    },
  }
}

export const useCustomFieldsCollection = (enabled = true) => {
  const { uuid } = useWorkspace()
  const params = customFieldsCollectionParams(uuid)

  return useReadCustomFields({ params, select: selectCustomFields, enabled })
}
